import React, { useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useStacklineTheme } from '@stackline/ui';
import { Box } from '@mui/material';
import { BluePlus, DocumentIcon, SnackbarCheckmark, SmallCancelIcon, CloseIcon } from 'src/components/SvgIcons';
import { validateFile, parseUploadedItems } from 'src/components/Search/AdvancedSearch/BulkUpload/BulkUploadDialog';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDialog, SlDialogRoot } from 'src/components/BeaconV2Components/SlDialog';
import color from 'src/utils/colors';

const MAX_INPUT = 500;

interface RetailerSkuBulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (skus: string[]) => void;
}

export const RetailerSkuBulkUploadModal: React.FC<RetailerSkuBulkUploadModalProps> = ({ open, onClose, onSave }) => {
  const theme = useStacklineTheme();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState(false);
  const [input, setInput] = useState('');
  const inputCount = input.split('\n').filter(Boolean).length;

  React.useEffect(() => {
    setInput('');
    setUploadedFile(null);
    setUploadError(false);
  }, [open]);

  const handleInputChange = (value: string) => {
    setInput(value);
    // Clear file if user starts typing
    if (uploadedFile) {
      setUploadedFile(null);
      setUploadError(false);
    }
  };

  const handleSave = async () => {
    try {
      const skus = await parseUploadedItems(input, uploadedFile);
      onSave(skus);
      onClose();
    } catch (err) {
      setUploadError(true);
    }
  };

  const handleClear = () => {
    setInput('');
    setUploadedFile(null);
    setUploadError(false);
  };

  const uploadMessage = uploadError
    ? 'The uploaded file does not meet the required specifications. Please try again.'
    : 'Upload complete. Your file was successfully uploaded.';

  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width="752px" height="641px">
        <Flex height="100%" flexDirection="column" position="relative">
          {/* Header Section with Top Divider */}
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="72px"
            position="relative"
            sx={{ borderBottom: `1px solid ${color.graysSecondary}` }}
          >
            <Text sx={{ fontWeight: 700, fontSize: '20px', lineHeight: '28px', textAlign: 'center' }}>Bulk Upload</Text>
            <Box position="absolute" right="24px" top="24px">
              <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                <CloseIcon style={{ width: '24px', height: '24px' }} />
              </button>
            </Box>
          </Flex>

          {/* Content Section */}
          <Flex
            flexDirection="column"
            paddingX={theme.spacing.xl}
            paddingTop="24px"
            paddingBottom="24px"
            flex="1"
            overflow="auto"
          >
            {/* Description */}
            <Flex flexDirection="column" gap={8}>
              <Text sx={{ fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}>Bulk SKU Upload</Text>
              <Text variant="body2" sx={{ lineHeight: '20px' }}>
                Paste up to 500 retailer SKUs into the dialog box below, or upload a CSV file containing a single list.
                <br />
                Files should include retailer SKUs only, with one per line, and no commas or additional formatting.
              </Text>
            </Flex>

            {/* Paste retailer SKUs Section */}
            <Flex paddingTop="24px" flexDirection="column" width="656px">
              <Flex paddingBottom="8px">
                <Text sx={{ fontWeight: 500, fontSize: '16px', lineHeight: '24px' }}>Paste retailer SKUs</Text>
              </Flex>
              <Box
                sx={{
                  border: `1px solid ${color.graysSecondary}`,
                  borderRadius: '4px',
                  backgroundColor: uploadedFile ? color.graysPrimary : 'white',
                  padding: '6px 4px 6px 14px',
                  height: '72px',
                  opacity: uploadedFile ? 0.5 : 1
                }}
              >
                <textarea
                  value={input}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder="Paste retailer SKUs here"
                  disabled={!!uploadedFile}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    outline: 'none',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: color.darkBlue,
                    resize: 'none',
                    backgroundColor: 'transparent',
                    margin: 0,
                    padding: 0,
                    overflowY: 'scroll',
                    cursor: uploadedFile ? 'not-allowed' : 'text'
                  }}
                  className="retailer-sku-paste-input"
                />
                <style>{`
                  .retailer-sku-paste-input::placeholder {
                    color: #BEC7D3;
                  }
                  .retailer-sku-paste-input::-webkit-scrollbar {
                    width: 5px;
                  }
                 .retailer-sku-paste-input::-webkit-scrollbar-thumb {
                    background: ${color.blueGrey};
                    border-radius: 20px;
                  }
                `}</style>
              </Box>
              <Flex justifyContent="flex-end" paddingTop="8px">
                <Text
                  sx={{
                    fontSize: '10px',
                    fontWeight: 500,
                    color: inputCount > MAX_INPUT ? theme.colors.error : theme.colors.primary
                  }}
                >
                  {inputCount > MAX_INPUT ? '-' : ''}{Math.abs(MAX_INPUT - inputCount)} retailer SKUs remaining
                </Text>
              </Flex>

              {/* Upload CSV Section */}
              <Flex flexDirection="column" marginTop="16px" alignItems="center">
                <Text
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                    marginBottom: '8px',
                    alignSelf: 'flex-start'
                  }}
                >
                  Upload CSV file
                </Text>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="656px"
                  border={`1px solid ${color.graysSecondary}`}
                  height="120px"
                  borderRadius="4px"
                  sx={{
                    backgroundColor: input ? color.graysPrimary : 'white',
                    opacity: input ? 0.5 : 1,
                    pointerEvents: input ? 'none' : 'auto'
                  }}
                >
                  <button style={{ background: 'none', border: 'none', cursor: input ? 'not-allowed' : 'pointer' }}>
                    <label htmlFor="file-input" style={{ cursor: input ? 'not-allowed' : 'pointer' }}>
                      {uploadedFile ? (
                        <Flex alignItems="center" gap="sm">
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="24px"
                            height="24px"
                            borderRadius="50px"
                            sx={{ backgroundColor: theme.colors.primary }}
                          >
                            <DocumentIcon style={{ fill: '#ffffff', width: '18px', height: '18px' }} />
                          </Box>
                          <Text variant="body2">{uploadedFile.name}</Text>
                        </Flex>
                      ) : (
                        <BluePlus role="button" style={{ width: theme.spacing.mdl, height: theme.spacing.mdl }} />
                      )}
                    </label>
                    <input
                      onChange={(evt) => {
                        const file = evt.target.files?.[0];
                        if (file) {
                          const errMsg = validateFile(file);
                          if (errMsg) {
                            setUploadError(true);
                          } else {
                            setUploadedFile(file);
                            setUploadError(false);
                            // Clear text input when file is uploaded
                            setInput('');
                          }
                        }
                      }}
                      style={{ display: 'none' }}
                      id="file-input"
                      type="file"
                      accept=".csv,.txt"
                      disabled={!!input}
                    />
                  </button>
                </Box>

                {/* Upload Status Message */}
                {(uploadedFile || uploadError) && (
                  <Flex gap={4} alignItems="center" marginTop="12px" justifyContent="center">
                    {uploadError ? (
                      <SmallCancelIcon style={{ fill: theme.colors.error, width: '24px', height: '24px' }} />
                    ) : uploadedFile ? (
                      <SnackbarCheckmark style={{ width: '24px', height: '24px' }} />
                    ) : null}

                    <Text
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '18px',
                        color: uploadError ? theme.colors.error : color.brandClubGreen
                      }}
                    >
                      {uploadMessage}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>

          {/* Bottom Section with Divider and Buttons */}
          <Flex
            flexDirection="column"
            sx={{ borderTop: `1px solid ${color.graysSecondary}` }}
            paddingX={theme.spacing.xl}
            paddingY="24px"
          >
            <Flex justifyContent="space-between" alignItems="center">
              <SlButton onClick={handleClear} variant="normal">
                Clear
              </SlButton>
              <SlButton
                onClick={handleSave}
                disabled={(inputCount <= 0 || inputCount > MAX_INPUT) && !uploadedFile}
                variant="contained"
              >
                Save
              </SlButton>
            </Flex>
          </Flex>
        </Flex>
      </SlDialog>
    </SlDialogRoot>
  );
};

